import fetcher from '@/services/fetcher'
import type { GamePlayData, GameSessionPageData } from '@/types'
import { toPortalUrl } from '@/utils/url-generation-utils'

/**
 * Service to handle game session operations
 */
export class GamePlaySession {
  /**
   * Play a game session and get ticket results
   *
   * @param gameSessionId - The game session ID
   * @param playCount - Number of plays to make
   * @returns Promise with game play data (played entries, remaining plays, etc.)
   */
  static async playGame(
    gameSessionId: string,
    playCount: number = 1
  ): Promise<GamePlayData> {
    try {
      const { data: response, error } = await fetcher.post(
        toPortalUrl('instantwin/game/play'),
        {
          game_session_id: gameSessionId,
          play_count: playCount,
        }
      )

      if (error.value) {
        throw new Error(error.value.message || 'Failed to play game session')
      }

      if (!response.value) {
        throw new Error('Invalid response from game play endpoint')
      }

      // Validate response structure
      if (!response.value.csi_content?.data?.status) {
        throw new Error('Invalid response format from game play endpoint')
      }

      if (response.value.csi_content.data.status !== 'success') {
        throw new Error(
          `Game play failed with status: ${response.value.csi_content.data.status}`
        )
      }

      // Return just the data portion, hiding the csi_content structure from components
      return response.value.csi_content.data
    } catch (error: any) {
      throw new Error(error.message || 'Failed to play game session')
    }
  }

  /**
   * Get game session data and current state
   *
   * @param gameSessionId - The game session ID
   * @returns Promise with game session page data (game session, played entries, etc.)
   */
  static async getSession(gameSessionId: string): Promise<GameSessionPageData> {
    try {
      const { data: response, error } = await fetcher.get(
        toPortalUrl(`instantwin/game/session/${gameSessionId}`)
      )

      if (error.value) {
        throw new Error(error.value.message || 'Failed to get game session')
      }

      if (!response.value) {
        throw new Error('Invalid response from game session endpoint')
      }

      // Validate response structure
      if (!response.value.csi_content?.data?.status) {
        throw new Error('Invalid response format from game session endpoint')
      }

      if (response.value.csi_content.data.status !== 'success') {
        throw new Error(
          `Game session fetch failed with status: ${response.value.csi_content.data.status}`
        )
      }

      // Return just the data portion, hiding the csi_content structure from components
      return response.value.csi_content.data
    } catch (error: any) {
      throw new Error(error.message || 'Failed to get game session')
    }
  }
}
